import React, { useEffect, useRef, useState } from 'react';
import { Box, Alert as JoyAlert, CircularProgress, Typography } from '@mui/joy';

interface TerritoryZipMapProps {
  zipCodes: string[];
  height?: string | number;
}

// Module-level cache — geocoded ZIPs persist across component mounts.
const zipCentroidCache = new Map<string, { lat: number; lng: number }>();

/**
 * Renders a Google Map with a purple dot at the centroid of each ZIP in
 * `zipCodes`. Uses the Google Geocoder to resolve ZIP → lat/lng on demand
 * (cached across mounts, so navigating away and back is instant).
 */
export const TerritoryZipMap: React.FC<TerritoryZipMapProps> = ({
  zipCodes,
  height = 350,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resolvedCount, setResolvedCount] = useState(0);

  useEffect(() => {
    let isMounted = true;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;
    setLoading(true);
    setError(null);

    const init = async () => {
      if (!isMounted || !containerRef.current) return;
      if (!window.google || !window.google.maps) {
        if (++attempts >= 30) {
          setError('Map unavailable. Refresh the page to try again. Your storm details are still available below.');
          setLoading(false);
          return;
        }
        retryTimer = setTimeout(init, 500);
        return;
      }

      const map = new google.maps.Map(containerRef.current, {
        center: { lat: 39.5, lng: -98.35 }, // Geographic center of the US
        zoom: 4,
        mapTypeControl: true,
        streetViewControl: false,
        fullscreenControl: true,
        zoomControl: true,
      });
      mapRef.current = map;

      if (!zipCodes.length) {
        setLoading(false);
        return;
      }

      const geocoder = new google.maps.Geocoder();
      const bounds = new google.maps.LatLngBounds();
      let resolved = 0;

      await Promise.all(
        zipCodes.map(
          (zip) =>
            new Promise<void>((resolve) => {
              const cached = zipCentroidCache.get(zip);
              if (cached) {
                addMarker(map, cached.lat, cached.lng, zip, bounds);
                resolved += 1;
                resolve();
                return;
              }

              geocoder.geocode(
                {
                  componentRestrictions: { country: 'US', postalCode: zip },
                },
                (results, status) => {
                  if (!isMounted) { resolve(); return; }
                  if (status === 'OK' && results && results[0]) {
                    const loc = results[0].geometry.location;
                    const latLng = { lat: loc.lat(), lng: loc.lng() };
                    zipCentroidCache.set(zip, latLng);
                    addMarker(map, latLng.lat, latLng.lng, zip, bounds);
                    resolved += 1;
                  }
                  resolve();
                },
              );
            }),
        ),
      );

      if (!isMounted) return;

      setResolvedCount(resolved);
      if (resolved > 0) {
        if (resolved === 1) {
          map.setCenter(bounds.getCenter());
          map.setZoom(11);
        } else {
          map.fitBounds(bounds, 48);
        }
      }
      setLoading(false);
    };

    const addMarker = (
      map: google.maps.Map,
      lat: number,
      lng: number,
      zip: string,
      bounds: google.maps.LatLngBounds,
    ) => {
      const marker = new google.maps.Marker({
        map,
        position: { lat, lng },
        title: zip,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: '#7c3aed', // Purple as requested
          fillOpacity: 0.95,
          strokeColor: '#ffffff',
          strokeWeight: 2,
        },
      });
      markersRef.current.push(marker);
      bounds.extend({ lat, lng });
    };

    init().catch((err) => {
      if (!isMounted) return;
      console.error('TerritoryZipMap init failed:', err);
      setError('Failed to load map');
      setLoading(false);
    });

    return () => {
      isMounted = false;
      clearTimeout(retryTimer);
      markersRef.current.forEach((m) => m.setMap(null));
      markersRef.current = [];
    };

  }, [zipCodes.join(',')]);

  if (error) {
    return <JoyAlert color="danger" sx={{ m: 2 }}>{error}</JoyAlert>;
  }

  return (
    <Box sx={{ position: 'relative', height, borderRadius: 'md', overflow: 'hidden' }}>
      {loading && (
        <Box
          sx={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: 'background.level1', zIndex: 1,
          }}
        >
          <CircularProgress size="md" />
        </Box>
      )}
      <Box ref={containerRef} sx={{ width: '100%', height: '100%' }} />
      {!loading && zipCodes.length > 0 && resolvedCount < zipCodes.length && (
        <Typography
          level="body-xs"
          sx={{
            position: 'absolute', bottom: 4, left: 8,
            bgcolor: 'background.body', px: 1, borderRadius: 'sm',
          }}
        >
          Showing {resolvedCount} of {zipCodes.length} ZIPs
        </Typography>
      )}
    </Box>
  );
};

export default TerritoryZipMap;
